import { useCallback, useMemo, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuthCore } from '../../core/modules/auth';
import { ERROR } from '../../core/constants/errorCode';
import { WELCOME } from '../../core/router/routes';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isEmpty } from 'lodash';

interface FormData {
  email: string;
  password: string;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email('The format of the email is not correct')
    .required('You must enter an email'),
  password: yup.string().required('You must enter a password'),
});

const defaultValues: FormData = {
  email: '',
  password: '',
};

const SignIn = () => {
  const { t } = useTranslation();
  const { token, errorCode, login } = useAuthCore();

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, dirtyFields, errors } = formState;

  const [showPassword, setShowPassword] = useState(false);

 const onSubmit = useCallback(
    ({ email, password }: FormData) => {
      login({ email, password }, reset);
    },
    [login, reset]
 );

const errorMessage = useMemo(() => {
    if (errorCode) {
     if (errorCode === ERROR.ERR_401) {
        return 'User or Password is incorrect';
      } else {
        return 'An unexpected error ocurred, try to login in a few minutes';
      }
    }
  }, [errorCode]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

 if (token) {
   return <Navigate to={WELCOME} replace />;
 }

  return (
    <div className="flex flex-col sm:flex-row items-center md:items-start sm:justify-center md:justify-start flex-1 min-w-0">
      <Paper className="h-full sm:h-auto md:flex md:items-center md:justify-end w-full sm:w-auto md:h-full md:w-1/2 py-8 px-16 sm:p-48 md:p-64 sm:rounded-2xl md:rounded-none sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1">
        <div className="w-full max-w-320 sm:w-320 mx-auto sm:mx-0">
          <div className="flex justify-center md:justify-start">
            <img
              className="h-48"
              src={`${process.env.PUBLIC_URL}/assets/images/logo/fulllogo.png`}
              alt="logo"
            />
          </div>
          <form
            name="loginForm"
            noValidate
            className="flex flex-col justify-center w-full mt-32"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="mb-24">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('label.email')}
                    autoFocus
                    type="email"
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    required
                    fullWidth
                  />
                )}
              />
            </div>
            <div className="mb-24">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('label.password')}
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    required
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </div>

            <Button
              variant="contained"
              color="secondary"
              className="w-full"
              sx={{ borderRadius: 50 }}
              disabled={isEmpty(dirtyFields) || !isValid}
              type="submit"
              size="large"
            >
              {t('signin.button.submit')}
            </Button>
          </form>

          {
            errorMessage && (
           <div className="text-center mt-8">
             <Typography variant="caption" className="text-red-500">
               {errorMessage}
             </Typography>
           </div>
            )
          }
        </div>
      </Paper>

      <Box
        className="relative hidden md:flex flex-auto items-center justify-center h-full p-64 lg:px-112 overflow-hidden"
        sx={{ backgroundColor: 'primary.main' }}
      >
        <div
          className="w-full h-full absolute h-100 w-100 inset-0 bg-cover bg-bottom pointer-events-none "
          style={{'backgroundImage': `url(${process.env.PUBLIC_URL}/assets/images/background.png)`}}
        >
        </div>
aaa
        <div className="z-10 relative w-full max-w-2xl text-center">
          <div className="text-7xl font-bold leading-none text-gray-100">
            <div>Maximiza la precisión de tus inventarios con IA</div>
          </div>
        </div>
      </Box>
    </div>
  );
};

export default SignIn;
